/**
 * Tab: Subscriptions — liga channel ↔ filtros (rule_kind, equipment, severity).
 *
 * Sem subscription, um canal não recebe nada.
 *
 * As linhas de canal `voice` são destacadas. Não é enfeite: o custo de errar
 * aqui não é o mesmo em todos os canais. Uma inscrição de e-mail a mais
 * produz uma mensagem; uma inscrição de voz a mais produz um telefone
 * tocando às três da manhã, e insistindo até alguém atender. Quem está
 * escolhendo precisa ver isso ANTES de clicar, não depois.
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Phone, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

import { subscriptionsApi } from "@/api/alerts";
import { apiErrorMessage } from "@/api/client";
import {
  useAlertSubscriptions,
  useEquipments,
  useNotificationChannels,
} from "@/hooks/queries";
import { KINDS_NOTIFICAVEIS, opcoesDeKind } from "@/lib/ruleKinds";
import type {
  AlertSubscription,
  AlertSubscriptionCreate,
  RuleKind,
  Severity,
} from "@/types";

import { Button } from "@/components/Button";
import { DataTable, type Column } from "@/components/DataTable";
import { Modal } from "@/components/Modal";
import { Field, Select } from "@/components/Field";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/Badge";
import { Tip } from "@/components/Tip";
import { SEVERIDADE_MINIMA_OPCOES } from "@/lib/severidade";

// Derivado, nunca escrito à mão: esta lista já ficou com 6 dos 12 kinds e
// ninguém percebeu, porque a tela não tem como saber o que falta nela.
// Inclui os cross-device — eles não são criáveis como regra, mas notificam,
// e filtrar notificação é exatamente o que esta tela faz.
const RULE_KINDS: { value: string; label: string }[] = [
  { value: "", label: "Todas as regras" },
  ...opcoesDeKind(KINDS_NOTIFICAVEIS),
];

export function SubscriptionsTab({ tenantId }: { tenantId?: string }) {
  const subs = useAlertSubscriptions(undefined, tenantId);
  const channels = useNotificationChannels(tenantId);
  const equipments = useEquipments(tenantId ? { tenant_id: tenantId } : undefined);
  const qc = useQueryClient();

  const [isCreating, setIsCreating] = useState(false);
  const [editTarget, setEditTarget] = useState<AlertSubscription | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AlertSubscription | null>(null);

  const channelById = new Map((channels.data ?? []).map((c) => [c.id, c]));
  const equipmentNameById = new Map(
    (equipments.data ?? []).map((e) => [e.id, e.name]),
  );

  const createMut = useMutation({
    mutationFn: (p: AlertSubscriptionCreate) => subscriptionsApi.create(p),
    onSuccess: () => {
      toast.success("Subscription criada");
      void qc.invalidateQueries({ queryKey: ["alert-subscriptions"] });
      setIsCreating(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // Editar, e não apagar-e-recriar: entre uma coisa e outra o canal fica sem
  // receber nada, e num canal de voz essa janela cai justamente no momento em
  // que alguém está mexendo na configuração de emergência.
  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AlertSubscriptionCreate }) =>
      subscriptionsApi.update(id, {
        rule_kind:    patch.rule_kind,
        equipment_id: patch.equipment_id,
        min_severity: patch.min_severity,
      }),
    onSuccess: () => {
      toast.success("Subscription atualizada");
      void qc.invalidateQueries({ queryKey: ["alert-subscriptions"] });
      setEditTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => subscriptionsApi.remove(id),
    onSuccess: () => {
      toast.success("Subscription deletada");
      void qc.invalidateQueries({ queryKey: ["alert-subscriptions"] });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const columns: Column<AlertSubscription>[] = [
    {
      key: "channel",
      header: "Canal",
      cell: (r) => {
        const ch = channelById.get(r.channel_id);
        const ehVoz = ch?.kind === "voice";
        return (
          <span className="flex items-center gap-2">
            {ehVoz && (
              <Phone className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            )}
            <span
              className={
                ehVoz
                  ? "font-medium text-amber-700 dark:text-amber-300"
                  : "font-medium text-slate-900 dark:text-slate-100"
              }
            >
              {ch?.name ?? r.channel_id.slice(0, 8)}
            </span>
            {ehVoz && (
              <Badge tone="warning">liga</Badge>
            )}
          </span>
        );
      },
    },
    {
      key: "rule_kind",
      header: "Tipo de regra",
      cell: (r) => (
        <Badge tone="brand">
          {r.rule_kind
            ? RULE_KINDS.find((k) => k.value === r.rule_kind)?.label ?? r.rule_kind
            : "qualquer"}
        </Badge>
      ),
    },
    {
      key: "equipment",
      header: "Ativo",
      cell: (r) =>
        r.equipment_id ? (
          <span className="text-xs text-slate-600 dark:text-slate-400">
            {equipmentNameById.get(r.equipment_id) ?? r.equipment_id.slice(0, 8)}
          </span>
        ) : (
          <span className="text-xs text-slate-500 dark:text-slate-400">qualquer</span>
        ),
    },
    {
      key: "min_severity",
      header: "Severidade mínima",
      cell: (r) => <Badge tone="default">{r.min_severity}</Badge>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <span className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            icon={<Pencil className="h-4 w-4" />}
            aria-label="Editar subscription"
            onClick={() => setEditTarget(r)}
          />
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 className="h-4 w-4 text-red-500" />}
            aria-label="Deletar subscription"
            onClick={() => setDeleteTarget(r)}
          />
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => setIsCreating(true)}>
          Nova subscription
        </Button>
      </div>

      <DataTable
        data={subs.data}
        columns={columns}
        loading={subs.isLoading}
        rowKey={(r) => r.id}
        emptyTitle="Nenhuma subscription"
        emptyDescription="Sem subscription, canais não recebem alertas. Liga pelo menos uma."
      />

      <SubscriptionFormModal
        open={isCreating || !!editTarget}
        editando={editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
        }}
        channelOptions={(channels.data ?? []).map((c) => ({ value: c.id, label: `${c.name} (${c.kind})` }))}
        equipmentOptions={[
          { value: "", label: "(qualquer ativo)" },
          ...(equipments.data ?? []).map((e) => ({ value: e.id, label: e.name })),
        ]}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(p) => {
          if (editTarget) updateMut.mutate({ id: editTarget.id, patch: p });
          else createMut.mutate(p);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deletar subscription?"
        message="O canal não vai mais receber alertas com este filtro."
        confirmLabel="Deletar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- form modal ------------------------------------------------------

function SubscriptionFormModal({
  open, editando, onClose, channelOptions, equipmentOptions, submitting, onSubmit,
}: {
  open: boolean;
  /** Preenchido = edição; null = criação. */
  editando: AlertSubscription | null;
  onClose: () => void;
  channelOptions: { value: string; label: string }[];
  equipmentOptions: { value: string; label: string }[];
  submitting?: boolean;
  onSubmit: (p: AlertSubscriptionCreate) => void;
}) {
  const [channelId, setChannelId] = useState(channelOptions[0]?.value ?? "");
  const [ruleKind, setRuleKind] = useState<string>("");
  const [equipmentId, setEquipmentId] = useState<string>("");
  const [minSeverity, setMinSeverity] = useState<Severity>("warning");

  useEffect(() => {
    if (!open) return;
    if (editando) {
      setChannelId(editando.channel_id);
      setRuleKind(editando.rule_kind ?? "");
      setEquipmentId(editando.equipment_id ?? "");
      setMinSeverity(editando.min_severity as Severity);
    } else {
      setChannelId(channelOptions[0]?.value ?? "");
      setRuleKind("");
      setEquipmentId("");
      setMinSeverity("warning");
    }
  }, [open, editando, channelOptions]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      channel_id:    channelId,
      rule_kind:     (ruleKind || null) as RuleKind | null,
      equipment_id:  equipmentId || null,
      min_severity:  minSeverity,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editando ? "Editar subscription" : "Nova subscription"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="sub-form" loading={submitting}>
            {editando ? "Salvar" : "Criar"}
          </Button>
        </>
      }
    >
      <form id="sub-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Trocar o canal de uma inscrição existente não é editar: é outra
            inscrição. Na edição o campo fica visível (pra saber do que se
            está falando) e travado. */}
        <Field label="Canal" required>
          <Select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            required
            disabled={!!editando}
            options={channelOptions}
            placeholder={channelOptions.length === 0 ? "Crie um canal primeiro" : undefined}
          />
        </Field>

        <Field
        label={
              <>
                Tipo de regra
                <Tip label="Tipo de regra">
                  Em branco, este canal recebe todos os tipos de alerta — inclusive os
                  que forem criados depois.
                </Tip>
              </>
            }
      >
          <Select
            value={ruleKind}
            onChange={(e) => setRuleKind(e.target.value)}
            options={RULE_KINDS}
          />
        </Field>

        <Field
        label={
              <>
                Ativo
                <Tip label="Ativo">
                  Em branco, este canal recebe alertas de qualquer ativo do cliente.
                </Tip>
              </>
            }
      >
          <Select
            value={equipmentId}
            onChange={(e) => setEquipmentId(e.target.value)}
            options={equipmentOptions}
          />
        </Field>

        <Field label="Severidade mínima" required>
          <Select
            value={minSeverity}
            onChange={(e) => setMinSeverity(e.target.value as Severity)}
            options={SEVERIDADE_MINIMA_OPCOES}
          />
        </Field>
      </form>
    </Modal>
  );
}
